import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';

import { ScheduleGridProvider } from './ScheduleGridContext';
import { ScheduleGridDayColumn } from './ScheduleGridDayColumn';
import { ScheduleGridHeader } from './ScheduleGridHeader';
import { ScheduleGridItem } from './ScheduleGridItem';
import { ScheduleGridTimeColumn } from './ScheduleGridTimeColumn';
import { ScheduleGridItemData, ScheduleGridProps, SizeThresholds } from './types';
import { getGridBodyHeight } from './utils';

const DEFAULT_HOUR_HEIGHT = 64;
const DEFAULT_GRID_LINES_PER_HOUR = 2;
const TIME_COLUMN_WIDTH = 48;

export const DEFAULT_SIZE_THRESHOLDS: SizeThresholds = {
  minimal: 10,
  compact: 32,
};

export const ScheduleGrid = <T,>({
  days,
  timeRange,
  hourHeight = DEFAULT_HOUR_HEIGHT,
  gridLinesPerHour = DEFAULT_GRID_LINES_PER_HOUR,
  items,
  showTimeColumn = true,
  sizeThresholds = DEFAULT_SIZE_THRESHOLDS,
  ItemComponent,
  TooltipComponent,
}: ScheduleGridProps<T>) => {
  const itemsByDay = useMemo(() => {
    const map = items.reduce<Map<string, ScheduleGridItemData<T>[]>>(
      (m, item) => m.set(item.day, [...(m.get(item.day) ?? []), item]),
      new Map(days.map((day) => [day.value, []])),
    );

    for (const dayItems of map.values()) {
      dayItems.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
    return map;
  }, [items, days]);

  const gridBodyHeight = useMemo(
    () => getGridBodyHeight(timeRange, hourHeight),
    [timeRange, hourHeight],
  );

  const gridTemplateColumns = showTimeColumn
    ? `${TIME_COLUMN_WIDTH}px repeat(${days.length}, 1fr)`
    : `repeat(${days.length}, 1fr)`;

  return (
    <ScheduleGridProvider
      hourHeight={hourHeight}
      gridLinesPerHour={gridLinesPerHour}
      timeRange={timeRange}
      sizeThresholds={sizeThresholds}
    >
      <Stack
        sx={(theme) => ({
          width: '100%',
          overflow: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '8px',
          paddingRight: theme.spacing(2.75),
          paddingBottom: theme.spacing(2.75),
        })}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns,
            gridTemplateRows: `auto ${gridBodyHeight}px`,
            minWidth: 0,
          }}
        >
          <ScheduleGridHeader days={days} showTimeColumn={showTimeColumn} />
          {showTimeColumn && (
            <Box sx={{ minHeight: 0 }}>
              <ScheduleGridTimeColumn />
            </Box>
          )}
          {days.map((day) => {
            const dayItems = itemsByDay.get(day.value) ?? [];
            return (
              <ScheduleGridDayColumn key={day.value}>
                {dayItems.map((item) => (
                  <ScheduleGridItem
                    key={item.id}
                    item={item}
                    ItemComponent={ItemComponent}
                    TooltipComponent={TooltipComponent}
                  />
                ))}
              </ScheduleGridDayColumn>
            );
          })}
        </Box>
      </Stack>
    </ScheduleGridProvider>
  );
};
